import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  FileText, 
  Download, 
  Eye, 
  Search, 
  Filter,
  ArrowUpDown,
  Trash2,
  Edit,
  X,
  Calendar,
  Tag,
  User
} from 'lucide-react';

const DocumentVaultPage = ({ onGoToGenerator }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isDocumentPreviewOpen, setIsDocumentPreviewOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Load documents from localStorage
  useEffect(() => {
    if (user?.email) {
      const savedDocuments = localStorage.getItem(`document_vault_${user.email}`);
      if (savedDocuments) {
        try {
          const parsedDocuments = JSON.parse(savedDocuments);
          setDocuments(parsedDocuments);
        } catch (error) {
          console.error('Error parsing saved documents:', error);
        }
      }
    }
  }, [user?.email]);

  // Document categories for filtering
  const categories = [
    { id: 'all', label: 'All Categories' },
    { id: 'Administrative Process', label: 'Administrative Process' },
    { id: 'Status Declaration', label: 'Status Declaration' },
    { id: 'Traffic Matters', label: 'Traffic Matters' },
    { id: 'Equity Relief', label: 'Equity Relief' },
    { id: 'Protective Notice', label: 'Protective Notice' },
    { id: 'Administrative Correction', label: 'Administrative Correction' },
    { id: 'Property Rights', label: 'Property Rights' },
    { id: 'Legal Authorization', label: 'Legal Authorization' },
    { id: 'Jurisdictional Challenge', label: 'Jurisdictional Challenge' },
    { id: 'AI Generated', label: 'AI Generated' }
  ];

  // Filter and sort documents
  const filteredAndSortedDocuments = documents
    .filter(doc => {
      const matchesSearch = doc.templateTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (doc.formData?.fullName || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filterCategory === 'all' || getDocumentCategory(doc.templateId) === filterCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'title':
          return a.templateTitle.localeCompare(b.templateTitle);
        default:
          return 0;
      }
    });

  // Get document category based on template ID
  const getDocumentCategory = (templateId) => {
    const categoryMap = {
      'conditional_acceptance': 'Administrative Process',
      'affidavit_status': 'Status Declaration',
      'traffic_discharge': 'Traffic Matters',
      'writ_mandamus': 'Equity Relief',
      'cease_desist': 'Protective Notice',
      'status_correction': 'Administrative Correction',
      'property_claim': 'Property Rights',
      'power_attorney': 'Legal Authorization',
      'plea_abatement': 'Jurisdictional Challenge',
      'ai_generated': 'AI Generated'
    };
    return categoryMap[templateId] || 'Administrative Process';
  };

  // Get category color
  const getCategoryColor = (category) => {
    const colorMap = {
      'Administrative Process': 'bg-blue-100 text-blue-800',
      'Status Declaration': 'bg-green-100 text-green-800',
      'Traffic Matters': 'bg-red-100 text-red-800',
      'Equity Relief': 'bg-purple-100 text-purple-800',
      'Protective Notice': 'bg-orange-100 text-orange-800',
      'Administrative Correction': 'bg-yellow-100 text-yellow-800',
      'Property Rights': 'bg-indigo-100 text-indigo-800',
      'Legal Authorization': 'bg-pink-100 text-pink-800',
      'Jurisdictional Challenge': 'bg-gray-100 text-gray-800',
      'AI Generated': 'bg-purple-100 text-purple-800'
    };
    return colorMap[category] || 'bg-gray-100 text-gray-800';
  };

  // Document preview functions
  const openDocumentPreview = (document) => {
    setSelectedDocument(document);
    setIsDocumentPreviewOpen(true);
  };

  const closeDocumentPreview = () => {
    setSelectedDocument(null);
    setIsDocumentPreviewOpen(false);
  };

  // Download functions
  const downloadAsPDF = async (content, filename) => {
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/documents/download-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          content: content,
          filename: filename
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `${filename}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        throw new Error('PDF generation failed');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const downloadAsDOCX = async (content, filename) => {
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/documents/download-docx`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          content: content,
          filename: filename
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `${filename}.docx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        throw new Error('DOCX generation failed');
      }
    } catch (error) {
      console.error('Error downloading DOCX:', error);
      alert('Error generating DOCX. Please try again.');
    }
  };

  // Delete document
  const deleteDocument = (documentId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      const updatedDocuments = documents.filter(doc => doc.id !== documentId);
      setDocuments(updatedDocuments);
      localStorage.setItem(`document_vault_${user?.email}`, JSON.stringify(updatedDocuments));
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 bg-blue-50">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Document Vault</h1>
            <p className="text-sm text-gray-600">Manage and view your generated legal documents</p>
          </div>
          <div className="flex items-center space-x-4">
            <Badge variant="outline" className="text-blue-700 border-blue-300">
              <FileText className="w-3 h-3 mr-1" />
              {documents.length} Documents
            </Badge>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="p-6 border-b border-gray-200 bg-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-4">
            {/* Category Filter */}
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div className="flex items-center space-x-2">
              <ArrowUpDown className="w-4 h-4 text-gray-500" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="title">Title A-Z</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Documents Grid */}
      <div className="flex-1 overflow-y-auto p-6">
        {filteredAndSortedDocuments.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Documents Found</h3>
            <p className="text-gray-600 mb-4">
              {documents.length === 0 
                ? "You haven't generated any documents yet. Create your first document in the Remedy Generator. Then share the Public Link of your Notice with the World."
                : "No documents match your current search and filter criteria."
              }
            </p>
            {documents.length === 0 && (
              <Button 
                onClick={() => onGoToGenerator ? onGoToGenerator() : navigate('/dashboard/remedy-agents')}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Go to Document Generator
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAndSortedDocuments.map((doc) => (
              <Card key={doc.id} className="hover:shadow-lg transition-shadow duration-200">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg text-gray-900 mb-2 line-clamp-2">
                        {doc.templateTitle}
                      </CardTitle>
                      <Badge className={`text-xs ${getCategoryColor(getDocumentCategory(doc.templateId))}`}>
                        {getDocumentCategory(doc.templateId)}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {/* Document Info */}
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      {new Date(doc.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                    {doc.formData?.fullName && (
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-2" />
                        {doc.formData.fullName}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openDocumentPreview(doc)}
                      className="flex-1 mr-2"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteDocument(doc.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Document Preview Modal */}
      {isDocumentPreviewOpen && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{selectedDocument.templateTitle}</h3>
                <p className="text-sm text-gray-600">
                  Created: {new Date(selectedDocument.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={closeDocumentPreview}
                className="ml-4 hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 max-h-96 overflow-y-auto">
              <div 
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: selectedDocument.generatedContent || 'Document content loading...' }}
              />
            </div>
            
            <div className="p-4 bg-gray-50 border-t">
              <div className="flex items-center justify-end space-x-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => downloadAsPDF(
                    selectedDocument.generatedContent, 
                    `${selectedDocument.templateTitle.replace(/[^a-zA-Z0-9]/g, '_')}`
                  )}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => downloadAsDOCX(
                    selectedDocument.generatedContent, 
                    `${selectedDocument.templateTitle.replace(/[^a-zA-Z0-9]/g, '_')}`
                  )}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download DOCX
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentVaultPage;